import { useEffect, useState, useContext, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { request } from "../../../utils/api";
import { AuthContext } from "../../../auth/AuthContext";
import DataTable from "react-data-table-component";
import InvoiceCard from "./InvoiceCard";
import Loading from "../utilComponent/Loading";
import WarehouseActivityModal from "../modal/WarehouseActivityModal";
import * as Yup from "yup";

const addRowSchema = Yup.object().shape({
  product_qty: Yup.number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than 0")
    .required("Quantity is required"),
  deposit: Yup.number()
    .typeError("Deposit must be a number")
    .min(0, "Deposit cannot be negative")
    .nullable(),
  payment: Yup.number()
    .typeError("Payment must be a number")
    .min(0, "Payment cannot be negative")
    .nullable(),
  recurring_type: Yup.string()
    .oneOf(["none", "weekly", "monthly"], "Invalid recurring type")
    .required("Recurring type is required"),
});

const ShipmentInvoice = () => {
  const { id: warehouseId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [warehouseIdState, setWarehouseId] = useState(warehouseId || "");
  const [warehouseSheetId, setWarehouseSheetId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [newRow, setNewRow] = useState({
    product_id: "",
    product_qty: "",
    deposit: "",
    payment: "",
    notes: "",
    recurring_type: "none",
  });
  const [totals, setTotals] = useState({
    total_label_qty: 0,
    total_deposit: 0,
    total_payment: 0,
    total_balance: 0,
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
  });

  const isAdmin = user?.role_key === "admin";
  const isSuperAdmin = user?.role_key === "super_admin";

  /*
   *  UI + calculations fallback:
   * if shipment_cost is 0/null/undefined/"" => treat as 2 for display & totals
   */
  const getEffectiveShipmentCost = (row) => {
    const sc = row?.shipment_cost;
    if (sc === "" || sc === null || sc === undefined || Number(sc) === 0)
      return 2;
    return Number(sc);
  };

  const fetchData = useCallback(
    async ({
      selectedWarehouseId = "",
      page = pagination.current_page,
      perPage = pagination.per_page,
    } = {}) => {
      try {
        setLoading(true);

        const baseUrl = "labels-warehouse-wise";
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(perPage),
        });

        const wid = warehouseId || selectedWarehouseId;
        if (wid) params.append("warehouse_id", String(wid));

        if (user?.role_key === "super_admin") {
          params.append("account_id", String(user?.account?.id || user.id));
        }

        const response = await request({
          url: `${baseUrl}?${params.toString()}`,
          method: "GET",
        });

        if (response?.status) {
          const items = (response.data?.items || []).map((row) => ({
            ...row,
            shipment_cost:
              row.shipment_cost === "" ||
              row.shipment_cost === null ||
              row.shipment_cost === undefined
                ? 0
                : Number(row.shipment_cost),

            extra_cost:
              row.extra_cost === "" ||
              row.extra_cost === null ||
              row.extra_cost === undefined
                ? 0
                : Number(row.extra_cost),
          }));

          setRows(items);
          setPagination((prev) => ({
            ...prev,
            current_page: response.data?.pagination?.current_page || page,
            per_page: response.data?.pagination?.per_page || perPage,
            total: response.data?.pagination?.total || 0,
            last_page: response.data?.pagination?.last_page || prev.last_page,
          }));
          setTotals(response.data?.totals || {});
          setWarehouseSheetId(response.data?.sheet_id || null);
        }
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    },
    [pagination.current_page, pagination.per_page, warehouseId, user],
  );

  const handleInputChange = (id) => {
    if (!id) return;
    setWarehouseId(id);
    setPagination((p) => ({ ...p, current_page: 1 }));
    fetchData({ selectedWarehouseId: id, page: 1, perPage: pagination.per_page });
  };

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin) {
      fetchData();
      return;
    }

    if (warehouseId) {
      fetchData({ selectedWarehouseId: warehouseId });
    }
  }, [isAdmin, isSuperAdmin, fetchData, warehouseId]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchData({ page, perPage: pagination.per_page });
  };

  const handleRowsPerPageChange = (perPage, page) => {
    const nextPage = page || 1;

    setPagination((prev) => ({
      ...prev,
      current_page: nextPage,
      per_page: perPage,
    }));

    fetchData({ page: nextPage, perPage });
  };

  const updateShipmentCost = (rowId, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.date === rowId
          ? { ...row, shipment_cost: value === "" ? 0 : Number(value) }
          : row,
      ),
    );
  };

  const updateExtraCost = (rowId, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.date === rowId
          ? { ...row, extra_cost: value === "" ? 0 : Number(value) }
          : row,
      ),
    );
  };

  const handleSave = async (row) => {
    try {
      const formData = new FormData();
      formData.append("warehouse_id", row.warehouse_id);
      formData.append("date", row.date);
      formData.append("shipment_cost", row.shipment_cost ?? 0);
      formData.append("extra_cost", row.extra_cost ?? 0);

      const response = await request({
        url: "warehouse-extra-cost",
        method: "POST",
        data: formData,
      });

      if (response?.status) {
        toast.success("Costs saved");
        fetchData({
          selectedWarehouseId: row.warehouse_id,
          page: pagination.current_page,
          perPage: pagination.per_page,
        });
      } else {
        toast.error("Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const columns = [
    {
      name: "Label Qty",
      selector: (row) => row.label_qty,
      center: true,
    },
    {
      name: "Date",
      selector: (row) => row.date || "-",
      sortable: true,
    },
    {
      name: "Shipment Cost",
      cell: (row) =>
        isAdmin || isSuperAdmin ? (
          <input
            type="number"
            className="form-control form-control-sm text-end"
            value={getEffectiveShipmentCost(row)}
            onChange={(e) => updateShipmentCost(row.date, e.target.value)}
          />
        ) : (
          <span>{getEffectiveShipmentCost(row)}</span>
        ),
    },
    {
      name: "Total Shipment Cost",
      selector: (row) => row.label_qty * getEffectiveShipmentCost(row),
      sortable: true,
    },
    {
      name: "Extra Cost",
      cell: (row) => (
        <input
          type="number"
          className="form-control form-control-sm text-end"
          value={row.extra_cost ?? 0}
          onChange={(e) => updateExtraCost(row.date, e.target.value)}
        />
      ),
    },
    {
      name: "Total Cost",
      selector: (row) =>
        row.label_qty * getEffectiveShipmentCost(row) +
        (Number(row.extra_cost) || 0),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          className="btn btn-sm btn-success"
          onClick={() => handleSave(row)}
        >
          Save
        </button>
      ),
      button: true,
    },
  ];

  const getTotalShipmentCost = () =>
    rows.reduce((t, r) => t + r.label_qty * getEffectiveShipmentCost(r), 0);

  const getTotalCost = () =>
    rows.reduce(
      (t, r) =>
        t +
        r.label_qty * getEffectiveShipmentCost(r) +
        (Number(r.extra_cost) || 0),
      0,
    );

  const getProductWarehouse = async () => {
    try {
      const response = await request({
        url: "warehouses",
        method: "GET",
      });

      setWarehouses(
        (response.data?.items || []).map((item) => ({
          warehouse_name: item.name,
          id: item.id,
          email: item.user_email,
        })),
      );
    } catch {
      toast.error("Error fetching warehouses");
    }
  };

  useEffect(() => {
    getProductWarehouse();
  }, []);

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const addDeposit = () => {
    setShowAddModal(true);
  };

  const closeAddCalculationRowModal = () => {
    setShowAddModal(false);
    setNewRow({
      product_id: "",
      product_qty: "",
      payment: "",
      deposit: "",
      notes: "",
      recurring_type: "none",
    });
  };

  const addCalculationRow = async () => {
    try {
      setErrors({});
      const payload = {
        ...newRow,
        recurring_type: newRow.recurring_type || "none",
      };

      await addRowSchema.validate(payload, { abortEarly: false });
      setAdding(true);
      const fd = new FormData();
      fd.append("recurring_type", payload.recurring_type);
      fd.append("user_id", user.id);
      fd.append("warehouse_id", warehouseIdState);
      fd.append("deposit", newRow.deposit || 0);
      fd.append("payment", newRow.payment || 0);
      fd.append("detail", newRow.notes || "");
      fd.append("product_qty", 0);
      fd.append("date", selectedDate || new Date().toISOString().split("T")[0]);
      if(isSuperAdmin){
        fd.append("account_id", user?.account?.id);
      }

      await request({
        url: "warehouse-cost-fields-sheets",
        method: "POST",
        data: fd,
      });

      toast.success("Row added successfully", { autoClose: 3000 });
      closeAddCalculationRowModal();
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        return;
      }

      toast.error("Failed to add row", { autoClose: 3000 });
    } finally {
      setAdding(false);
    }
  };

  const handleNewRowChange = (e) => {
    const { name, value } = e.target;
    setNewRow((prev) => ({ ...prev, [name]: value }));
  };

  const viewDeposit = () => {
    navigate(`/shipment-leasure/${warehouseIdState}`);
  };

  return (
    <>
      <ToastContainer />
      {showModal && (
        <WarehouseActivityModal
          show={showModal}
          onHide={handleCloseModal}
          row={selectedRow}
        />
      )}

      {showAddModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Add Invoice Row</h5>
                  <button
                    className="btn-close"
                    onClick={closeAddCalculationRowModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label>Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-12">
                      <label>Qty</label>
                      <input
                        type="number"
                        className="form-control"
                        name="product_qty"
                        value={newRow.product_qty}
                        onChange={handleNewRowChange}
                      />
                      {errors.product_qty && (
                        <div className="invalid-feedback">
                          {errors.product_qty}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label>Deposit</label>
                      <input
                        type="number"
                        className="form-control"
                        name="deposit"
                        value={newRow.deposit}
                        onChange={handleNewRowChange}
                      />
                      {errors.deposit && (
                        <div className="invalid-feedback">{errors.deposit}</div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label>Payment</label>
                      <input
                        type="number"
                        className="form-control"
                        name="payment"
                        value={newRow.payment}
                        onChange={handleNewRowChange}
                      />
                      {errors.payment && (
                        <div className="invalid-feedback">{errors.payment}</div>
                      )}
                    </div>

                    <div className="col-12">
                      <label>Detail</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        name="notes"
                        value={newRow.notes}
                        onChange={handleNewRowChange}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Recurring</label>
                      <select
                        className={`form-control ${errors.recurring_type ? "is-invalid" : ""}`}
                        name="recurring_type"
                        value={newRow.recurring_type}
                        onChange={handleNewRowChange}
                      >
                        <option value="none">None</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      {errors.recurring_type && (
                        <div className="invalid-feedback">
                          {errors.recurring_type}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeAddCalculationRowModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={addCalculationRow}
                    disabled={adding}
                  >
                    {adding ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <div className="container-fluid">
        <div className="page-toolbar py-3">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">Calculation</li>
            <li className="breadcrumb-item active">Warehouse Calculation</li>
          </ol>
        </div>

        <div className="card shadow-sm">
          <div className="card-header">
            <h6 className="m-0">Warehouse Calculation List</h6>
          </div>

          {(isSuperAdmin || isAdmin) && (
            <div className="card mx-3">
              <select
                name="warehouse_id"
                className="form-select border-start-0 focus-effect"
                onChange={(e) => handleInputChange(e.target.value)}
                defaultValue=""
              >
                <option value="">Select Warehouse</option>
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.warehouse_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {warehouseIdState && (isSuperAdmin || isAdmin) && (
            <div className="mt-3 mx-3 d-flex justify-content-end">
              <button
                className="btn btn-sm btn-success"
                style={{ marginRight: "8px" }}
                onClick={viewDeposit}
              >
                View Deposit
              </button>
              <button className="btn btn-sm btn-success" onClick={addDeposit}>
                Add Deposit
              </button>
            </div>
          )}
          <div className="row mt-4 g-3 mx-2">
            {[
              ["Total Labels", totals.total_label_qty],
              ["Total Payment", totals.total_payment],
              ["Total Deposit", totals.total_deposit],
              ["Total Balance", totals.total_balance],
              ["Total Shipment Cost", totals.total_shipment_cost],
              ["Total Cost", totals.grandtotalShipExtraCost],
            ].map(([label, value]) => (
              <div key={label} className="col-md-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    {label === "Total Deposit" && (
                      <button
                        className="btn btn-sm btn-light border mb-2"
                        style={{ marginRight: "6px" }}
                        onClick={() =>
                          handleOpenModal({ warehouse_id: warehouseIdState })
                        }
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    )}
                    <small className="text-muted">{label}</small>
                    <h4 className="fw-bold mt-1">{value}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={rows}
              keyField="id"
              striped
              responsive
              highlightOnHover
              expandableRows
              expandableRowsComponent={({ data }) => (
                <InvoiceCard
                  isCallFrom="warehouseInvoice"
                  data={data.products?.flatMap((p) => p.labels) || []}
                />
              )}
              progressPending={loading}
              progressComponent={<Loading />}
              pagination
              paginationServer
              paginationTotalRows={pagination.total}
              paginationPerPage={pagination.per_page}
              paginationDefaultPage={pagination.current_page}
              paginationRowsPerPageOptions={[15, 25, 30, 50]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ShipmentInvoice;
